import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListRowType, ListProps, ListColumnType, ListColumnOptionType, ListColumnPreferenceEntry, QueryParamType, ListMetaDataType, ListTooltipType, SingleFilter, QueryType, ListFilterType } from 'types/ListTypes';
import { BulkSelectionState } from 'types/BulkActionTypes';
import { useInitializeParams, setBrowserUrl, executeQueryParamsUpdate, stringifyParams } from './functions/queryParamsUtils';
import { toggleSelectionId, toggleSelectAllState, getSelectedCount } from './functions/selectionUtils';
import { useFetchData } from 'services/api/useFetchData';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import ListContext from './ListContext';
import ListComponent from './ListComponent';
import ListColumnsModal from './ListColumnsModal';
import { format, startOfWeek } from 'date-fns';
import { useFetchContext } from 'services/api/FetchContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useActiveFeature } from 'services/permissions/permissionChecks';
import { buildActiveFiltersFromQueryParams, buildFilterQueryParams, fetchFilterDropdownData } from './functions/filterUtils';
import { mergeColumnsWithPreference, estimateColumnsWidth, estimateRequiredTableWidth, convertColumnsToPixelWidths, getPseudoColumnsWidth, getListTierContentWidth, resolveListWidthTier, ListWidthTier } from './functions/columnPreferences';
import { encryptData, decryptData } from 'services/authentication/encryptData';

const AbstractList = <T extends ListRowType>({
    query, objectName, filters, filterFields, quickFilterSize, columns, additionalColumns, extendedWidth = false, showSearch, showDeleteOrDeactivate, showAddButton, showImport, showExport, showPerWeek, headerTitle,
    explanationText, showColumnSelector, onRowClick, openPageUrl, formModal, editModal, postNewItem, detailModal, detailModalName, tableClassName, actionButton,
    releaseViewModal, wizardModal, detailPageUrl, modalSize, detailModalSize, isPaginated, pageSize = 50, isDraggable, buttonSize, groupByField, backButton, secondaryButton,
    changeViewButton, draggingApiUrl, renderHeaderInfo, renderTrailingInfo, renderNotice, getFormModal, getModalSize, getFormModalTitle, setRefetchList, allowEditingInactiveRows, bulkActions, moreMenu
}: ListProps<T>) => {
    const history = useHistory();
    const location = useLocation();

    const { getCache, setCache } = useFetchContext();
    const { activeFeatures, allowedRights, handleLogout, userDivision } = useAuthContext();
    const { initializeModal } = useModal();
    const { userLocale, getTimeFormat } = useSettings();
    const { t } = useTranslation();
    const isExtendedWidth = extendedWidth && activeFeatures.includes('extended_lists');

    // Determine if this list supports the user chosen columns feature
    const hasCustomListColumns = useActiveFeature('custom_list_columns') && Boolean(bulkActions?.objectKey);

    // Capture the initial browser url search params before they are merged with the defaults
    const initialSearchRef = useRef(location.search);

    // Check if there are quick filter set in the filters
    const quickFilters = Array.isArray(filters) ? (filters as SingleFilter[] | undefined)?.filter(filter => filter.quickFilter) ?? [] : [];
    const defaultQuickFilter = quickFilters.find(filter => filter.default) ?? null;

    // Set the initial params, from either the default quick filter or the main query
    const initialParams = defaultQuickFilter?.params ?? (query.defaultParams ?? {});

    // Set the query params, store the active query and execute fetch
    const [queryParams, setQueryParams] = useState(useInitializeParams(initialParams, isPaginated ?? false, location));
    const [activeQuery, setActiveQuery] = useState<QueryType>(() => ({ endpoint: defaultQuickFilter?.endpoint || query.endpoint, object: query.object }));

    // Add the page size to the request when a custom page size is given
    const fetchParams = useMemo(() => (
        isPaginated && pageSize !== 50 ? { ...queryParams, page_size: pageSize } : queryParams
    ), [queryParams, isPaginated, pageSize]);

    const { response, loading, refetch } = useFetchData({ apiUrl: activeQuery.endpoint, params: fetchParams });

    // Determine the week start date on the query params or fallback to the current date if not given
    const [weekStartDate, setWeekStartDate] = useState(() => {
        const params = new URLSearchParams(location.search);
        const weekStart = params.get('week_start');
        if (weekStart) {
            return new Date(weekStart);
        }
        return startOfWeek(new Date(), { weekStartsOn: 1 });
    });

    // Set the states
    const [data, setData] = useState<ListRowType[]>([])
    const [listMetaData, setListMetaData] = useState<ListMetaDataType>({ currentPage: 1, totalRows: 0, totalPages: 0 })
    const [extraMeta, setExtraMeta] = useState<Record<string, any> | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [lastSearchTerm, setLastSearchTerm] = useState<string | null>(null);
    const [ordering, setOrdering] = useState<string | null>(() => new URLSearchParams(location.search).get('ordering'));

    // Keep track of the column whose sorting was cleared, it shows the no sorting state in its header
    const [clearedOrderingColumn, setClearedOrderingColumn] = useState<string | null>(null);
    const [activeQuickFilter, setActiveQuickFilter] = useState<SingleFilter | null>(defaultQuickFilter);
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
    const [filterDropdownData, setFilterDropdownData] = useState<Record<string, any>>({});
    const [availableColumns, setAvailableColumns] = useState<ListColumnType[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<ListColumnType[]>([]);
    const [listTooltip, setListTooltip] = useState<ListTooltipType | null>(null);

    // Reads the cached column configuration so the first render already uses the stored widths
    const readColumnConfigCache = (): { columns: ListColumnOptionType[], preference: ListColumnPreferenceEntry[] | null, ordering: string | null } | null => {
        if (!hasCustomListColumns) {
            return null;
        }
        try {
            const cachedValue = localStorage.getItem(`list_columns_${bulkActions!.objectKey}`);
            return cachedValue ? JSON.parse(decryptData(cachedValue)) : null;
        } catch (error) {
            return null;
        }
    };

    // Writes the column configuration cache used by the initial render of this list
    const writeColumnConfigCache = (columns: ListColumnOptionType[] | null, preference: ListColumnPreferenceEntry[] | null, ordering: string | null) => {
        if (!hasCustomListColumns) {
            return;
        }
        try {
            const cacheString = JSON.stringify({ columns: columns ?? [], preference, ordering });
            localStorage.setItem(`list_columns_${bulkActions!.objectKey}`, encryptData(cacheString));
        } catch (error) {
            // Ignore storage errors, the cache is only a rendering optimization
        }
    };

    // Keep track of the column options and the stored column preference of the user
    const [columnOptions, setColumnOptions] = useState<ListColumnOptionType[] | null>(() => readColumnConfigCache()?.columns ?? null);
    const [columnPreference, setColumnPreference] = useState<ListColumnPreferenceEntry[] | null>(() => readColumnConfigCache()?.preference ?? null);

    // Keep track of the stored sorting of the user, the list opens sorted like this
    const [orderingPreference, setOrderingPreference] = useState<string | null>(() => readColumnConfigCache()?.ordering ?? null);

    // Keep track of the viewport width, the list layout depends on the container breakpoints
    const [viewportWidth, setViewportWidth] = useState<number>(() => window.innerWidth);

    // Follow the viewport width while the window is resized
    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Keep track of the bulk selection state, starts with nothing selected
    const [selection, setSelection] = useState<BulkSelectionState>({ mode: 'include', ids: new Set() });

    // Opens the bulk actions panel directly on this action, used by the more menu export
    const [panelInitialKind, setPanelInitialKind] = useState<'export' | null>(null);

    // Keep track of the inactive items toggle, initialized from the initial browser url params
    const [showInactive, setShowInactive] = useState<boolean>(() => {
        const activeParams = moreMenu?.inactiveItemsToggle?.activeParams;
        if (!activeParams) {
            return false;
        }

        // The toggle starts active when every toggle param is present in the initial url
        const initialParams = new URLSearchParams(initialSearchRef.current);
        return Object.entries(activeParams).every(([key, value]) => initialParams.get(key) === String(value));
    });

    // Filter columns based on active features
    const visibleColumns = useMemo(() => {
        const allColumns = [...(columns ?? []), ...(additionalColumns ?? [])];

        return allColumns.filter(column => {
            if (!column.requiredFeature) {
                return true;
            }
            return activeFeatures.includes(column.requiredFeature);
        });
    }, [columns, additionalColumns, activeFeatures]);

    useEffect(() => {
        // Get the current browser url search parameters
        const currentParams = new URLSearchParams(location.search);
        
        // Merge current browser url parameters with default query params of the list
        const paramsToSet = { ...query.defaultParams };
        for (const [key, value] of currentParams.entries()) {
            paramsToSet[key] = value;
        }

        // Set the browser url with current and default params
        setBrowserUrl(paramsToSet);
    }, []);

    // Keep track of whether the filters were already loaded on a previous run of the apply effect
    const filtersAppliedAfterLoadRef = useRef(false);

    // Apply active filters to query params to show in the url, also once the filters finish loading
    useEffect(() => {
        if (!filtersLoaded) {
            return;
        }

        // Skip the run caused by the filters loading when no filter is active, the url already holds the defaults
        const triggeredByLoad = !filtersAppliedAfterLoadRef.current;
        filtersAppliedAfterLoadRef.current = true;
        if (triggeredByLoad && Object.keys(activeFilters).length === 0) {
            return;
        }

        // Use the inactive items params as defaults while the toggle is active
        const inactiveParams = moreMenu?.inactiveItemsToggle?.activeParams;
        const defaultParams = showInactive && inactiveParams ? inactiveParams : query.defaultParams;

        // Clear both default param variants so the toggle switches cleanly
        const paramsToClear = [
            ...Object.keys(query.defaultParams ?? {}),
            ...Object.keys(inactiveParams ?? {}),
        ];

        // Generate the new params
        const newParams = buildFilterQueryParams({activeFilters, filterFields, queryParams, defaultParams, paramsToClear});

        // Keep the current page when the filters did not change, only a real filter change starts at page one
        const { page: newPage, ...newFilterParams } = newParams;
        const { page: currentPage, ...currentFilterParams } = queryParams;
        if (stringifyParams(newFilterParams) === stringifyParams(currentFilterParams)) {
            return;
        }

        // Update the params
        executeQueryParamsUpdate(newParams, queryParams, setQueryParams, history, location);
    }, [activeFilters, showInactive, filtersLoaded]);

    // Derive a signature of the filter field definitions, lists often rebuild the
    // filterFields array on every render so the identity of the array is not stable
    const filterFieldsSignature = useMemo(() => (
        JSON.stringify((filterFields ?? []).map(filterField => [
            filterField.name, filterField.queryParam, filterField.type, filterField.defaultValue, filterField.defaultValueFromContext
        ]))
    ), [filterFields]);

    // Sync query params from the url to active filters
    useEffect(() => {
        if (!filterFields?.length) {
            return;
        }

        // Generate the new active filters
        const newActiveFilters = buildActiveFiltersFromQueryParams({filterFields, queryParams});

        // Apply the configured filter defaults only when the page was opened without url params
        if (initialSearchRef.current === '') {
            filterFields.forEach(filterField => {
                if (newActiveFilters[filterField.name] !== undefined) {
                    return;
                }

                // Apply the plain default value of the field
                if (filterField.defaultValue !== undefined) {
                    newActiveFilters[filterField.name] = filterField.defaultValue;
                }

                // Apply the division of the logged in user as default value
                if (filterField.defaultValueFromContext === 'user_division' && userDivision && activeFeatures.includes('divisions')) {
                    newActiveFilters[filterField.name] = String(userDivision);
                }
            });
        }

        // Update the active filters, keep the current object when nothing changed so the apply effect stays quiet
        setActiveFilters(prev => stringifyParams(prev) === stringifyParams(newActiveFilters) ? prev : newActiveFilters);
    }, [filterFieldsSignature, userDivision]);

    // Fetch dropdown filter data
    useEffect(() => {
        fetchFilterDropdownData({
            filterFields,
            activeFeatures,
            getCache,
            setCache,
            handleLogout,
            setFilterDropdownData,
            setFiltersLoaded,
        });

    }, [filterFields]);

    useEffect(() => {
        if (response) {
            // In case of a draggable list, sort the results based on their ordering property
            if (isDraggable) {
                response.results.sort((a: T, b: T) => a.ordering - b.ordering);
            }

            // Set the data with the results
            setData(response.results);

            // Set the total items in the list meta data
            setListMetaData(prev => ({ ...prev, totalRows: response.count }))

            // Set the extra meta data from the response
            setExtraMeta(response.meta_data ?? null)

            // In case of a paginated list, calculate the total pages
            if (isPaginated) {
                setListMetaData(prev => ({ ...prev, totalPages: Math.ceil(response.count / pageSize) }))
            }
        }
    }, [response, isDraggable, isPaginated]);

    // Derive a signature of the active filter, excludes page/page_size/ordering
    // and includes the active query endpoint, used to reset the bulk selection
    const filterSignature = useMemo(() => {
        const { page, page_size, ordering, ...restParams } = queryParams;
        return JSON.stringify({ endpoint: activeQuery.endpoint, params: restParams });
    }, [queryParams, activeQuery.endpoint]);

    // Skip the very first run of the filter signature effect, that is the mount itself
    const isFirstFilterSignature = useRef(true);

    // Reset the bulk selection whenever the active filter actually changes
    useEffect(() => {
        if (isFirstFilterSignature.current) {
            isFirstFilterSignature.current = false;
            return;
        }
        setSelection({ mode: 'include', ids: new Set() });
    }, [filterSignature]);

    useEffect(() => {
        // Execute the search query after a delay of 750ms to prevent unwanted fetches
        const debounceSearch = setTimeout(() => {

            // Execute search when a search term exist
            if (searchTerm !== '' && searchTerm !== lastSearchTerm) {
                updateQueryParams({ search: searchTerm, page: 1 });
                setLastSearchTerm(searchTerm);

            // If a search is executed and made empty, remove the search param from the query
            } else if (lastSearchTerm && searchTerm === '') {
                updateQueryParams({ search: undefined, page: 1 });
                setLastSearchTerm(searchTerm);
            }
        }, 750);

        return () => clearTimeout(debounceSearch);
    }, [searchTerm, lastSearchTerm, queryParams, executeQueryParamsUpdate])

    // Update the query params with the week start date if the list has to show the items per week
    useEffect(() => {
        if (showPerWeek && weekStartDate) {
            updateQueryParams({ week_start: format(weekStartDate, 'yyyy-MM-dd'), page: 1 });
        }
    }, [weekStartDate]);
    
    // Fetch the column options and stored preference when the feature is active
    useEffect(() => {
        if (!hasCustomListColumns) {
            return;
        }

        const loadColumnOptions = async () => {
            const response = await fetchData({ apiUrl: `get_list_column_options/${bulkActions!.objectKey}`, handleLogout });
            if (response) {
                const preferenceColumns = response.preference?.columns?.length ? response.preference.columns : null;
                const preferenceOrdering = response.preference?.ordering ?? null;
                setColumnOptions(response.columns ?? []);
                setColumnPreference(preferenceColumns);
                setOrderingPreference(preferenceOrdering);

                // Cache the configuration so the next visit renders the right widths directly
                writeColumnConfigCache(response.columns ?? [], preferenceColumns, preferenceOrdering);
            }
        };
        loadColumnOptions();
    }, [hasCustomListColumns]);

    // Build the selected columns from the stored preference or the defaults
    useEffect(() => {
        if (hasCustomListColumns && columnPreference?.length && columnOptions) {
            setSelectedColumns(mergeColumnsWithPreference({
                preferenceColumns: columnPreference,
                defaultColumns: visibleColumns,
                columnOptions,
                listKey: bulkActions!.translationKey ?? bulkActions!.objectKey,
                userLocale,
                timeFormat: getTimeFormat(),
            }));
        } else {
            setSelectedColumns(visibleColumns);
        }
    }, [visibleColumns, columnPreference, columnOptions, hasCustomListColumns]);

    // Remember whether the stored sorting was already applied, it only applies once per visit
    const orderingPreferenceAppliedRef = useRef(false);

    // Open the list sorted like the stored sorting when the url holds no sorting of its own
    useEffect(() => {
        if (!hasCustomListColumns || !orderingPreference || orderingPreferenceAppliedRef.current) {
            return;
        }
        orderingPreferenceAppliedRef.current = true;

        // A sorting in the opened url wins over the stored sorting
        if (new URLSearchParams(initialSearchRef.current).has('ordering')) {
            return;
        }
        setOrdering(orderingPreference);
        updateQueryParams({ ordering: orderingPreference }, true);
    }, [orderingPreference, hasCustomListColumns]);

    // Saves or resets the column preference and refetches when the fields changed,
    // optionally applying the columns to every user of the company
    const saveColumnPreference = async (columns: ListColumnPreferenceEntry[] | null, refetchAfterSave: boolean = true, applyToEnvironment: boolean = false, ordering?: string | null) => {
        const data: Record<string, any> = { columns: columns ?? [], apply_to_environment: applyToEnvironment };

        // Only send the sorting when it changes, the backend keeps the stored sorting otherwise
        if (ordering !== undefined) {
            data.ordering = ordering;
        }
        await saveData({
            apiUrl: `patch_user_list_preference/${bulkActions!.objectKey}`,
            method: 'patch',
            data,
        });
        const newOrdering = ordering !== undefined ? ordering : orderingPreference;
        setColumnPreference(columns && columns.length ? columns : null);
        setOrderingPreference(newOrdering);

        // Keep the cached configuration in sync with the saved preference
        writeColumnConfigCache(columnOptions, columns && columns.length ? columns : null, newOrdering);

        // Refetch so the fk display values of new columns come along
        if (refetchAfterSave) {
            refetch();
        }
    };

    // Persist a new column width after dragging a header resize handle
    const handleColumnResizeEnd = (field: string, width: number) => {
        const baseEntries = columnPreference?.length
            ? columnPreference
            : selectedColumns.map(column => ({ field: column.field }));
        const newEntries = baseEntries.map(entry => (
            entry.field === field ? { ...entry, width: `${Math.round(width)}px` } : entry
        ));

        // A width change needs no refetch, the visible fields stay the same
        saveColumnPreference(newEntries, false);
    };

    // Persist the new column order after dragging a column header
    const handleColumnReorderEnd = (field: string, targetIndex: number) => {
        const baseEntries = columnPreference?.length
            ? columnPreference
            : selectedColumns.map(column => ({ field: column.field }));

        // Move the dragged field to the target position
        const fromIndex = baseEntries.findIndex(entry => entry.field === field);
        if (fromIndex === -1 || fromIndex === targetIndex) {
            return;
        }
        const newEntries = [...baseEntries];
        const [movedEntry] = newEntries.splice(fromIndex, 1);
        newEntries.splice(targetIndex, 0, movedEntry);

        // An order change needs no refetch, the visible fields stay the same
        saveColumnPreference(newEntries, false);
    };

    // Open the column selection modal from the list more menu
    const openColumnsModal = () => {
        if (!columnOptions) {
            return;
        }
        const currentEntries = columnPreference?.length
            ? columnPreference
            : visibleColumns.map(column => ({ field: column.field }));

        initializeModal(
            <ListColumnsModal
                listKey={bulkActions!.translationKey ?? bulkActions!.objectKey}
                columnOptions={columnOptions}
                defaultColumns={visibleColumns}
                currentColumns={currentEntries}
                canApplyToEnvironment={allowedRights.includes('change_settings')}
                onSave={(columns, applyToEnvironment) => saveColumnPreference(columns, true, applyToEnvironment, columns ? undefined : null)} />,
            { title: t('lists.columns_modal.title'), modalSize: 'small' }
        );
    };

    // The base layout of the list, extended lists start one layout wider
    const baseWidthTier: ListWidthTier = isExtendedWidth ? 'container-wide' : 'container-filled';

    // The content width of the base layout on a large screen, the flexible
    // columns take their default widths inside this width
    const defaultContentWidth = getListTierContentWidth(baseWidthTier, 1610);

    // The pixel width of the checkbox, drag and delete columns around the chosen columns
    const pseudoColumnsWidth = getPseudoColumnsWidth({
        hasSelectColumn: Boolean(bulkActions),
        isDraggable: Boolean(isDraggable),
        hasDeleteColumn: Boolean(showDeleteOrDeactivate),
    });

    // The width the chosen columns need before the list has to widen, only
    // a stored column preference can make a list outgrow its base layout
    const requiredTableWidth = useMemo(() => (
        hasCustomListColumns && columnPreference?.length
            ? estimateRequiredTableWidth(selectedColumns, visibleColumns, pseudoColumnsWidth, defaultContentWidth)
            : 0
    ), [selectedColumns, visibleColumns, hasCustomListColumns, columnPreference, pseudoColumnsWidth, defaultContentWidth]);

    // Pick the smallest layout the chosen columns fit in, the list only widens
    // once the columns no longer fit the current layout at this viewport width
    const tableWidthClass = useMemo<ListWidthTier>(() => (
        resolveListWidthTier(requiredTableWidth, viewportWidth, baseWidthTier)
    ), [requiredTableWidth, viewportWidth, baseWidthTier]);

    // In the full width layout the flexible columns become the fixed pixel
    // width they had in the base layout, so the default columns keep their
    // size and the leftover space goes to the trailing filler column
    const displayColumns = useMemo(() => {
        if (tableWidthClass !== 'container-wide-filled') {
            return selectedColumns;
        }
        return convertColumnsToPixelWidths(selectedColumns, visibleColumns, defaultContentWidth - pseudoColumnsWidth);
    }, [selectedColumns, visibleColumns, tableWidthClass, defaultContentWidth, pseudoColumnsWidth]);

    // The minimum pixel width of the table, exact in the full width layout and
    // the estimated required width otherwise so the table never outgrows its container
    const tableMinWidth = useMemo(() => (
        tableWidthClass === 'container-wide-filled'
            ? estimateColumnsWidth(displayColumns, pseudoColumnsWidth)
            : requiredTableWidth
    ), [displayColumns, tableWidthClass, requiredTableWidth, pseudoColumnsWidth]);

    // Handles refetching of the list, for example when deleting a row
    const refetchList = useCallback(() => {
        refetch();
    }, [refetch]);

    // Handles refetch triggered by a parent component
    useEffect(() => {
        if (setRefetchList) {
            setRefetchList(refetchList);
        }
    }, [setRefetchList]);

    // Handles the ordering change, when reordering an item in the list by dragging
    const handleDragOrdering = (newOrder: ListRowType[]) => {
        setData(newOrder);
    };

    // Handles the ordering change of a clicked column header, a click cycles
    // through ascending, descending and back to the default sorting of the list
    const handleColumnOrdering = (column: string) => {
        const newOrdering = ordering === column ? `-${column}` : ordering === `-${column}` ? null : column;
        setOrdering(newOrdering);
        setClearedOrderingColumn(newOrdering === null ? column : null);

        // An undefined ordering removes the param from the url, so the list uses its default sorting
        updateQueryParams({ ordering: newOrdering ?? undefined }, true);

        // Store the sorting in the list preference so the list opens sorted like this next time
        if (hasCustomListColumns) {
            const currentEntries = columnPreference?.length ? columnPreference : [];
            saveColumnPreference(currentEntries, false, false, newOrdering);
        }
    }

    // Handle page change of paginated lists
    const handlePageChange = (newPage: number) => {
        updateQueryParams({ page: newPage }, true);
        setListMetaData({ ...listMetaData, currentPage: newPage });

        // Clear the current rows so the loading rows show while the new page loads
        setData([]);
    };

    // Register a search change
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }

    // Handle the active quick filter change
    const handleActiveQuickFilterChange = (selectedOption: string) => {

        // Find the selected quick filter
        const selectedQuickFilter = quickFilters.find((filter => filter.id === selectedOption));

        if (selectedQuickFilter) {
            // Set the active quick filter
            setActiveQuickFilter(selectedQuickFilter);

            // Update the active query
            setActiveQuery({
                endpoint: selectedQuickFilter.endpoint || query.endpoint,
                object: query.object
            });

            // Configure the new params and reset the page
            const newParams = {
                ...(selectedQuickFilter.params ?? {}),
                page: 1,
            }

            // Force empty params if no params are set for the quick filter
            if (!selectedQuickFilter.params) {
                setOrdering(null);
                executeQueryParamsUpdate({}, queryParams, setQueryParams, history, location);
                return;
            }

            // Update params
            executeQueryParamsUpdate(newParams, queryParams, setQueryParams, history, location);
        }
    }

    // Select everything matching the current filters and open the panel on the export step
    const handleExportAll = () => {
        setSelection({ mode: 'exclude', ids: new Set() });
        setPanelInitialKind('export');
    };

    // Toggle the deleted or deactivated items view of the more menu
    const handleToggleInactiveItems = () => {
        // Clear the configured filter fields when the toggle switches on
        const clearFilterNames = moreMenu?.inactiveItemsToggle?.clearFilterNames;
        if (!showInactive && clearFilterNames?.length) {
            setActiveFilters(prev => {
                const newFilters = { ...prev };
                clearFilterNames.forEach(name => {
                    delete newFilters[name];
                });
                return newFilters;
            });
        }

        // Flip the toggle state
        setShowInactive(prev => !prev);
    };

    // Toggle a single row in the bulk selection, symmetric for both selection modes
    const toggleRowSelection = (id: number) => {
        setSelection(prev => toggleSelectionId(prev, id));
    };

    // Toggle the header checkbox between selecting everything and selecting nothing
    const toggleSelectAll = () => {
        setSelection(prev => toggleSelectAllState(prev));
    };

    // Derive the number of currently selected rows from the selection state
    const selectedCount = getSelectedCount(selection, listMetaData.totalRows);

    // Clear the pending panel action once the selection is emptied again
    useEffect(() => {
        if (selectedCount === 0 && panelInitialKind) {
            setPanelInitialKind(null);
        }
    }, [selectedCount]);

    // Execute the query params update function for the new parameters
    const updateQueryParams = (newParams: QueryParamType, keepCurrentParams?: boolean) => {
        let combinedParams;

        // When executing a page or ordering change, add the ordering- or page param to the current params
        if (keepCurrentParams) {
            combinedParams = { ...queryParams, ...newParams };
        
        // For other changes, just update the new params and reset the ordering
        } else {
            combinedParams = { ...queryParams, ...newParams };
            setOrdering(null);
        }

        executeQueryParamsUpdate(combinedParams, queryParams, setQueryParams, history, location);
    };

    return (
        <ListContext.Provider value={{ loading, queryParams, updateQueryParams, data, setData, listMetaData,
            setListMetaData, extraMeta, ordering, clearedOrderingColumn, searchTerm, setSearchTerm, lastSearchTerm, setLastSearchTerm,
            availableColumns, setAvailableColumns, selectedColumns, setSelectedColumns, displayColumns, tableMinWidth, listTooltip,
            setListTooltip, tableWidthClass, handlePageChange, handleSearchChange, handleColumnOrdering,
            handleDragOrdering, refetchList, selection, setSelection, toggleRowSelection, toggleSelectAll, selectedCount, objectName,
            hasCustomListColumns, openColumnsModal, handleColumnResizeEnd, handleColumnReorderEnd }}>
            <ListComponent
                query={query}
                objectName={objectName}
                tableClassName={tableClassName}
                tableWidthClass={tableWidthClass}
                bulkActions={bulkActions}
                moreMenu={moreMenu}
                inactiveItemsActive={showInactive}
                onToggleInactiveItems={handleToggleInactiveItems}
                onExportAll={handleExportAll}
                panelInitialKind={panelInitialKind}
                filters={filters}
                filterFields={filterFields}
                quickFilterSize={quickFilterSize}
                activeFilters={activeFilters}
                setActiveFilters={setActiveFilters}
                filterDropdownData={filterDropdownData}
                activeQuickFilter={activeQuickFilter}
                onActiveQuickFilterChange={handleActiveQuickFilterChange}
                columns={visibleColumns}
                groupByField={groupByField}
                additionalColumns={additionalColumns}
                explanationText={explanationText}
                showSearch={showSearch}
                showDeleteOrDeactivate={showDeleteOrDeactivate}
                allowEditingInactiveRows={allowEditingInactiveRows}
                showAddButton={showAddButton}
                showImport={showImport}
                showExport={showExport}
                showPerWeek={showPerWeek}
                weekNavigation={showPerWeek ? { weekStartDate, setWeekStartDate } : undefined }
                headerTitle={headerTitle}
                backButton={backButton}
                actionButton={actionButton}
                secondaryButton={secondaryButton}
                showColumnSelector={showColumnSelector}
                renderHeaderInfo={renderHeaderInfo}
                renderTrailingInfo={renderTrailingInfo}
                renderNotice={renderNotice}
                changeViewButton={changeViewButton}
                buttonSize={buttonSize}
                onRowClick={onRowClick}
                postNewItem={postNewItem}
                openPageUrl={openPageUrl}
                formModal={formModal}
                getFormModal={getFormModal}
                getFormModalTitle={getFormModalTitle}
                editModal={editModal}
                detailModal={detailModal}
                detailModalName={detailModalName}
                releaseViewModal={releaseViewModal}
                wizardModal={wizardModal}
                detailPageUrl={detailPageUrl}
                modalSize={modalSize}
                getModalSize={getModalSize}
                detailModalSize={detailModalSize}
                isPaginated={isPaginated}
                pageSize={pageSize}
                isDraggable={isDraggable}
                draggingApiUrl={draggingApiUrl}
                refetchList={refetchList} />
        </ListContext.Provider>
    );
};

export default AbstractList;